import { Heart } from "lucide-react";
import { cn } from "@/lib/utils";
import { useFavorites } from "./FavoritesProvider";
import type { FavoriteKind } from "@/lib/favorites/types";
import { farmlyToast } from "@/lib/farmlyToast";
import { track } from "@/lib/analytics";
import { useAuth } from "@/context/AuthContext";
import { useAuthSheet } from "@/components/auth/AuthSheetProvider";
import { useNavigate } from "react-router-dom";
import { buildLoginUrl } from "@/lib/redirect";

interface FavoriteButtonProps {
  kind: FavoriteKind;
  entityId: string;
  slug?: string;
  title?: string;
  imagePath?: string;
  size?: "sm" | "md";
  className?: string;
  location?: "card" | "detail" | "favorites_list";
}

export function FavoriteButton({
  kind,
  entityId,
  slug,
  title,
  imagePath,
  size = "md",
  className,
  location = "card",
}: FavoriteButtonProps) {
  const { isFavorited, toggleFavorite } = useFavorites();
  const { user } = useAuth();
  const authSheet = useAuthSheet();
  const navigate = useNavigate();
  const isAuthed = !!user;
  const saved = isFavorited(kind, entityId);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (!isAuthed) {
      // Primary: open auth sheet
      if (authSheet?.openLogin) {
        track("favorite_login_prompted", { kind, entity_id: entityId, location, method: "sheet" });
        track("favorite_login_clicked", { kind, entity_id: entityId, location, method: "sheet" });
        authSheet.openLogin();
        return;
      }

      // Fallback: toast with login action
      track("favorite_login_prompted", { kind, entity_id: entityId, location, method: "toast_fallback" });
      farmlyToast("Spara genom att logga in", {
        action: {
          label: "Logga in",
          onClick: () => {
            track("favorite_login_clicked", { kind, entity_id: entityId, location, method: "toast_fallback" });
            navigate(buildLoginUrl());
          },
        },
      });
      return;
    }

    // Authed flow
    const payload = { kind, entityId, slug, title, imagePath };
    toggleFavorite(payload);

    if (saved) {
      track("favorite_removed", { kind, entity_id: entityId, location });
      farmlyToast("Borttagen", {
        action: {
          label: "Ångra",
          onClick: () => {
            toggleFavorite(payload);
            track("favorite_added", { kind, entity_id: entityId, location });
          },
        },
      });
    } else {
      track("favorite_added", { kind, entity_id: entityId, location });
      farmlyToast("Sparad");
    }
  };

  const sizeClasses = size === "sm" ? "h-9 w-9" : "h-11 w-11";
  const iconSize = size === "sm" ? "h-4 w-4" : "h-5 w-5";

  return (
    <button
      onClick={handleClick}
      className={cn(
        "flex items-center justify-center rounded-full transition-all",
        "bg-background/80 backdrop-blur-sm hover:bg-background/95 shadow-sm",
        sizeClasses,
        className
      )}
      aria-label={saved ? "Ta bort från favoriter" : "Spara som favorit"}
      aria-pressed={saved}
    >
      <Heart
        className={cn(
          iconSize,
          "transition-colors",
          saved ? "fill-primary text-primary" : "text-foreground/70"
        )}
      />
    </button>
  );
}
